import { computed, ref, Ref } from 'vue';
import { CampaignType } from '@studyportals/campaign-management-api-interface';
import store from '@/store';

export default class UTCTimeIndicatorCode {
	private utcDate: Ref<Date | null> = ref(null);
	private timeoutId: Ref<NodeJS.Timeout | null> = ref(null);
	private intervalId: Ref<NodeJS.Timeout | null> = ref(null);

	public utcTime = computed((): string => {
		if (!this.utcDate.value) {
			return '';
		}

		return `${
			this.padNumber(this.utcDate.value.getUTCHours())
		}:${
			this.padNumber(this.utcDate.value.getUTCMinutes())
		}`;
	});

	public minutesUntilTwelveFifteen = computed((): number => {
		if (!this.utcDate.value) {
			return 0;
		}

		const utcHours = this.utcDate.value.getUTCHours();
		const utcMinutes = this.utcDate.value.getUTCMinutes();
		if (utcHours === 0 && utcMinutes < 15) {
			return 15 - utcMinutes;
		}

		const twentyFourHoursInMinutes = 24 * 60;
		return twentyFourHoursInMinutes + 15 - (utcHours * 60 + utcMinutes);
	});

	public shouldUtcWarningBeShown = computed((): boolean => {
		if (!this.utcDate.value || this.campaignType.value === CampaignType.CUSTOM_PRODUCTS) {
			return false;
		}

		const utcHours = this.utcDate.value.getUTCHours();
		const utcMinutes = this.utcDate.value.getUTCMinutes();
		// Only show between 23:30 and 23:50.
		return utcHours === 23 && utcMinutes >= 30 && utcMinutes < 50;
	});

	public shouldUtcErrorBeShown = computed((): boolean => {
		if (!this.utcDate.value || this.campaignType.value === CampaignType.CUSTOM_PRODUCTS) {
			return false;
		}

		const utcHours = this.utcDate.value.getUTCHours();
		const utcMinutes = this.utcDate.value.getUTCMinutes();
		// Only show between 23:50 and 0:15.
		return (utcHours === 23 && utcMinutes >= 50) || (utcHours === 0 && utcMinutes < 15);
	});

	private secondsUntilNewMinuteStarts = computed((): number => {
		if (!this.utcDate.value) {
			return 0;
		}

		return 60 - this.utcDate.value.getUTCSeconds();
	});

	private campaignType = computed((): CampaignType | null => {
		return store.getters.campaignType();
	});

	public mounted(): void {
		this.updateUtcDate();
		this.updateTimeOnceNewMinuteStarts();
		this.makeAdjustmentsOnBrowserTabSwitching();
	}

	private makeAdjustmentsOnBrowserTabSwitching(): void {
		document.addEventListener('visibilitychange', () => {
			if (document.hidden) {
				return;
			}

			if (this.timeoutId.value) {
				clearTimeout(this.timeoutId.value);
			}
			if (this.intervalId.value) {
				clearInterval(this.intervalId.value);
			}

			this.updateUtcDate();
			this.updateTimeOnceNewMinuteStarts();
		});
	}

	private updateTimeOnceNewMinuteStarts(): void {
		const oneSecondOfMargin = 1000;
		this.timeoutId.value = setTimeout(() => {
			this.updateUtcDate();
			this.updateTimeEveryMinute();
		}, this.secondsUntilNewMinuteStarts.value * 1000 + oneSecondOfMargin);
	}

	private updateTimeEveryMinute(): void {
		this.intervalId.value = setInterval(() => {
			this.updateUtcDate();
		}, 60000);
	}

	private updateUtcDate(): void {
		this.utcDate.value = new Date();
	}

	private padNumber(value: number): string {
		return value.toString().padStart(2, '0');
	}
}
